import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { BookMarked, Pencil, FileDown, History, Loader2, ListChecks, ShieldAlert, Handshake, HelpCircle, Briefcase, Users } from "lucide-react";

const FIELDS = [
  { k: "job", label: "What the job is", icon: Briefcase, kind: "text", hint: "The work, the site, the size — in the client's words." },
  { k: "client_behaviour", label: "Who the client is and how they behave", icon: Users, kind: "text", hint: "How they run a bid, who decides, what they value." },
  { k: "asked", label: "What was asked", icon: HelpCircle, kind: "text", hint: "The ask: scope, price basis, deliverables, dates." },
  { k: "assumptions", label: "What we assumed", icon: ListChecks, kind: "list", hint: "One per line." },
  { k: "risks", label: "Risks we carried", icon: ShieldAlert, kind: "list", hint: "One per line." },
  { k: "bd_notes", label: "What BD knows", icon: Handshake, kind: "text", hint: "Relationship, history, standing." },
  { k: "open_items", label: "Open items", icon: ListChecks, kind: "list", hint: "One per line." },
];
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

function MemoryEditor({ open, onClose, bidId, context, onSaved }) {
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    const d = {};
    FIELDS.forEach((f) => { d[f.k] = f.kind === "list" ? (context?.[f.k] || []).join("\n") : (context?.[f.k] || ""); });
    setDraft(d);
  }, [open, context]);
  const save = async () => {
    setBusy(true);
    try {
      const body = {};
      FIELDS.forEach((f) => { body[f.k] = f.kind === "list" ? draft[f.k].split("\n").map((x) => x.trim()).filter(Boolean) : draft[f.k]; });
      await api.put(`/bids/${bidId}`, { context: body });
      toast.success("Project memory saved");
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="memory-editor">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BookMarked className="w-4 h-4 text-[#003E87]" /> Project memory</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">The team&rsquo;s memory of this project — not private notes. Nothing internal (rates, burden, markup, receipts) belongs here.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[62vh] overflow-y-auto pr-1">
          {FIELDS.map((f) => (
            <div key={f.k}>
              <Label className="text-xs text-slate-600">{f.label} <span className="text-slate-400 font-normal">· {f.hint}</span></Label>
              <textarea data-testid={`memory-field-${f.k}`} rows={f.kind === "list" ? 3 : 2} value={draft[f.k] || ""} onChange={(e) => setDraft((d) => ({ ...d, [f.k]: e.target.value }))}
                className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#003E87]/20" />
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button data-testid="memory-save-btn" disabled={busy} onClick={save} className="bg-[#003E87] hover:bg-[#00305F]">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save memory"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// §14: the structured memory block + the dated Project context.pdf versions kept in the Context folder.
export default function ProjectMemoryCard({ bidId, readOnly }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [writing, setWriting] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const load = useCallback(() => api.get(`/bids/${bidId}/context/versions`).then((r) => setData(r.data)).catch(() => setData({ versions: [], context: {} })), [bidId]);
  useEffect(() => { load(); }, [load]);
  const write = async () => {
    setWriting(true);
    try { const r = await api.post(`/bids/${bidId}/context/snapshot`, { milestone: "manual" }); toast.success(`Project context v${r.data.version} filed in the Context folder`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not write the context"); }
    finally { setWriting(false); }
  };
  if (!data) return null;
  const ctx = data.context || {};
  const filled = FIELDS.filter((f) => (f.kind === "list" ? ctx[f.k]?.length : ctx[f.k]));
  const versions = showAll ? data.versions : data.versions.slice(0, 3);
  return (
    <Card className="p-5" data-testid="project-memory-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-semibold text-black text-sm flex items-center gap-2"><BookMarked className="w-4 h-4 text-[#003E87]" /> Project memory</h3>
          <p className="text-xs text-slate-500 mt-0.5">What we knew and decided, written to <b>Project context.pdf</b> in the Context folder at every milestone{data.last_written ? ` · last written ${fmt(data.last_written)}` : ""}.</p>
          {(data.written_by || data.written_at) && (
            <p className="text-[11px] mt-1" data-testid="context-written-by">
              <span className="text-slate-500">Last written by <b className="text-slate-700">{data.written_by || "—"}</b>{data.written_at ? ` on ${fmt(data.written_at)}` : ""}</span>
              {data.stale && <span className="ml-2 inline-block text-[10px] font-semibold uppercase tracking-wide text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5" data-testid="context-stale">Stale · 30+ days</span>}
            </p>
          )}
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="memory-edit-btn"><Pencil className="w-3.5 h-3.5 mr-1.5" /> {filled.length ? "Edit" : "Write the memory"}</Button>
            <Button size="sm" onClick={write} disabled={writing} data-testid="memory-write-btn" className="bg-[#003E87] hover:bg-[#00305F] text-white">{writing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><FileDown className="w-3.5 h-3.5 mr-1.5" /> Write Project context.pdf</>}</Button>
          </div>
        )}
      </div>
      {filled.length === 0 ? (
        <div className="mt-3 text-sm text-slate-400" data-testid="memory-empty">Nothing written yet. The connector fills this as it works; anyone on the team can write it too.</div>
      ) : (
        <div className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-4">
          {filled.map((f) => (
            <div key={f.k} data-testid={`memory-${f.k}`} className={f.kind === "text" && (ctx[f.k] || "").length > 160 ? "sm:col-span-2" : ""}>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1 mb-1"><f.icon className="w-3.5 h-3.5 text-[#003E87]" /> {f.label}</div>
              {f.kind === "list" ? (
                <ul className="text-sm text-slate-700 space-y-0.5">{ctx[f.k].map((x, i) => <li key={i} className="flex gap-2"><span className="text-slate-300">•</span><span>{x}</span></li>)}</ul>
              ) : <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{ctx[f.k]}</p>}
            </div>
          ))}
        </div>
      )}
      <div className="mt-4 border-t border-slate-100 pt-3" data-testid="memory-versions">
        <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1 mb-1.5"><History className="w-3.5 h-3.5" /> Project context.pdf · {data.versions.length} version{data.versions.length === 1 ? "" : "s"}</div>
        {data.versions.length === 0 ? <div className="text-xs text-slate-400">Not written yet — it will be at the next milestone.</div> : (
          <ul className="space-y-1">
            {versions.map((v) => (
              <li key={v.document_id} className="text-xs flex items-center gap-2 flex-wrap" data-testid={`memory-version-${v.version}`}>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${v.document_id}/download`} target="_blank" rel="noreferrer" className="text-[#003E87] underline inline-flex items-center gap-1" data-testid={`memory-version-link-${v.version}`}><FileDown className="w-3 h-3" /> v{v.version} · {v.label}</a>
                <span className="text-slate-400">{fmt(v.at)} · {v.by}{v.note ? ` · “${v.note}”` : ""}</span>
              </li>
            ))}
            {data.versions.length > 3 && <button onClick={() => setShowAll((s) => !s)} className="text-[11px] text-slate-400 hover:text-slate-700" data-testid="memory-versions-toggle">{showAll ? "Show fewer" : `Show all ${data.versions.length}`}</button>}
          </ul>
        )}
      </div>
      <MemoryEditor open={editing} onClose={() => setEditing(false)} bidId={bidId} context={ctx} onSaved={load} />
    </Card>
  );
}
